import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

const CURRENT_VERSION = '1.0.0-alpha.1';
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const LOCAL_STORAGE_KEY = 'app_version';

interface VersionCheckResult {
  updateAvailable: boolean;
  latestVersion: string | null;
  forceUpdate: boolean;
  releaseNotes: string | null;
}

export const useVersionCheck = () => {
  const [versionInfo, setVersionInfo] = useState<VersionCheckResult>({
    updateAvailable: false,
    latestVersion: null,
    forceUpdate: false,
    releaseNotes: null,
  });

  const checkVersion = async () => {
    try {
      const { data, error } = await supabase
        .from('app_version')
        .select('version, force_update, release_notes, deployed_at')
        .order('deployed_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error checking version:', error);
        return;
      }
      
      if (!data) return;

      if (!data) return;

      const storedVersion = localStorage.getItem(LOCAL_STORAGE_KEY);
      const isFirstLoad = !storedVersion;

      if (isFirstLoad) {
        // First load - set version without showing notification
        localStorage.setItem(LOCAL_STORAGE_KEY, data.version);
        return;
      }

      const updateAvailable = storedVersion !== data.version;

      setVersionInfo({
        updateAvailable,
        latestVersion: data.version,
        forceUpdate: data.force_update || false,
        releaseNotes: data.release_notes,
      });
    } catch (error) {
      console.error('Version check failed:', error);
    }
  };

  const acknowledgeUpdate = () => {
    if (versionInfo.latestVersion) {
      localStorage.setItem(LOCAL_STORAGE_KEY, versionInfo.latestVersion);
    }
    setVersionInfo({
      updateAvailable: false,
      latestVersion: null,
      forceUpdate: false,
      releaseNotes: null,
    });
  };

  const refreshApp = () => {
    if (versionInfo.latestVersion) {
      localStorage.setItem(LOCAL_STORAGE_KEY, versionInfo.latestVersion);
    }
    window.location.reload();
  };

  useEffect(() => {
    // Check immediately on mount
    checkVersion();

    // Then check every 5 minutes
    const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  return {
    ...versionInfo,
    acknowledgeUpdate,
    refreshApp,
  };
};
